import { plural } from '@lingui/macro';

import { Display, Flex, Text } from '@rover/kibble/core';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';

import { getYearsOfExperience } from '../../utils/utils';

import ExperienceTrophyIcon from './ExperienceTrophyIcon';

type Props = {
  sitter: EnhancedPersonSitterProfile;
  iconSize?: number;
};

/**
 * Renders the sitter's "years of experience" leading with a large, bold count followed by
 * the icon and the rest of the label. Shown in the gallery widget in place of the
 * ratings/reviews summary when the sitter has no reviews yet.
 *
 * Reads the count straight from the provider profile so it shows regardless of the amount
 * of experience (see DEV-150426). Returns null when the sitter has no recorded years of
 * experience, letting callers fall back to the ratings placeholder.
 */
const FeaturedExperienceHighlight = ({ sitter, iconSize = 24 }: Props): JSX.Element | null => {
  const years = getYearsOfExperience(sitter);
  if (years < 1) return null;

  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      flexWrap="wrap"
      gap="2x"
      data-testid="experience-highlight"
    >
      <Flex flexDirection="row" alignItems="baseline" gap="2x">
        <Display size="400" mb="0x">
          {years}
        </Display>
        <ExperienceTrophyIcon size={iconSize} />
      </Flex>
      <Text size="200" fontWeight="regular">
        {plural(years, { one: 'year of experience', other: 'years of experience' })}
      </Text>
    </Flex>
  );
};

export default FeaturedExperienceHighlight;
